import { Link } from 'react-router'
import * as Mtds from '@mattilsynet/design/react'
import {
  CalendarBlankIcon,
  HashIcon,
  MapPinIcon,
  WarningIcon
} from '@phosphor-icons/react'
import {
  getResultStatus,
  getSampleBusinessDisplayName,
  getSampleStatus,
  hasSampleBeenCompromised
} from '../../utils/sampleUtils'
import { getFirstRequisition } from '../../utils/requisitionUtils'
import { getSubstanceText } from '../../utils/taskUtils'
import { SampleResultTag } from '../sample-result-tag/SampleResultTag'
import { SampleStatusTag } from '../sample-status-tag/SampleStatusTag'
import { toLocaleDateString } from '../../utils/dateUtils'
import type { Sample } from '../../types/types'

type SampleCardProps = {
  sample: Sample
  view: 'compact' | 'expanded'
}

const emptyValue = '-'

export function SampleCard({ sample, view }: Readonly<SampleCardProps>) {
  const requisition = getFirstRequisition(sample)
  const { analysisType, results } = requisition || {}
  const subSamplesCount = sample.subSamples?.length ?? 0
  const sampleStatus = getSampleStatus(sample)
  const resultStatus = getResultStatus(
    results,
    hasSampleBeenCompromised(sample)
  )
  const substanceText = getSubstanceText(analysisType, ' · ')
  const takenDate = toLocaleDateString(sample.takenDate) ?? 'Mangler dato'
  const productText = sample.sampleType.product ?? emptyValue
  const matrixText = sample.sampleType.matrix ?? emptyValue
  const samplingPlace = sample.sampntType?.nameNo ?? emptyValue
  const businessName = getSampleBusinessDisplayName(sample)
  const hasProcessingError = requisition?.latestProcessingError != null

  return (
    <Mtds.Card
      as={Link}
      to={`/samples/${sample.id}`}
      className='hover:bg-surface-hover'
    >
      <Mtds.Grid data-gap='2'>
        <Mtds.Flex data-gap='2' data-wrap data-align='center'>
          <Mtds.Heading data-size='xs' className='mr-auto'>
            <Mtds.Flex data-align='center' data-gap='1'>
              <HashIcon />
              {sample.id}
            </Mtds.Flex>
          </Mtds.Heading>
          <SampleStatusTag status={sampleStatus} />
          {resultStatus != null && <SampleResultTag status={resultStatus} />}
          {requisition?.followUp === true && (
            <Mtds.Tag data-color='warning'>Oppfølging</Mtds.Tag>
          )}
          {hasProcessingError && (
            <Mtds.Tag data-color='warning'>
              <WarningIcon aria-hidden />
              Feil under behandling
            </Mtds.Tag>
          )}
        </Mtds.Flex>
        {view === 'compact' && (
          <Mtds.Grid data-gap='2'>
            <Mtds.Flex data-justify='space-between' data-align='center'>
              <Mtds.Muted>
                <span className='inline-flex items-center gap-1'>
                  <CalendarBlankIcon aria-hidden />
                  {takenDate}
                </span>
              </Mtds.Muted>
              {subSamplesCount > 1 && (
                <Mtds.Muted>
                  <span className='inline-flex items-center'>
                    {subSamplesCount} delprøver
                  </span>
                </Mtds.Muted>
              )}
            </Mtds.Flex>
          </Mtds.Grid>
        )}
        {view === 'expanded' && (
          <Mtds.Grid data-gap='2'>
            <Mtds.Flex data-justify='space-between' data-align='center'>
              <strong>
                {productText}
                {matrixText !== emptyValue && ` > ${matrixText}`}
              </strong>
              {subSamplesCount > 1 && (
                <Mtds.Muted>
                  <span className='inline-flex items-center'>
                    {subSamplesCount} delprøver
                  </span>
                </Mtds.Muted>
              )}
            </Mtds.Flex>
            {analysisType == null ? (
              <Mtds.Tag data-color='danger' className='w-fit'>
                Mangler analysetype
              </Mtds.Tag>
            ) : (
              <Mtds.Muted>{substanceText ?? emptyValue}</Mtds.Muted>
            )}
            <Mtds.Flex data-gap='3' data-wrap data-align='center'>
              <Mtds.Muted>
                <span className='inline-flex items-center gap-1'>
                  <CalendarBlankIcon aria-hidden />
                  {takenDate}
                </span>
              </Mtds.Muted>
              <Mtds.Muted>
                <span className='inline-flex items-center gap-1'>
                  <MapPinIcon aria-hidden />
                  {samplingPlace}
                </span>
              </Mtds.Muted>
            </Mtds.Flex>
            {businessName != null && <Mtds.Muted>{businessName}</Mtds.Muted>}
          </Mtds.Grid>
        )}
      </Mtds.Grid>
    </Mtds.Card>
  )
}
